/** Maps API version `status` to list-page badge label. */
const VERSION_STATUS_TO_BADGE = {
  ACTIVE: "LIVE",
  DRAFT: "DRAFT",
  ARCHIVED: "ARCHIVED",
  PAUSED: "PAUSED"
};

/** Returns the workflow version with the highest version number. */
export function getMaxVersion(workflow) {
  const versions = workflow?.versions;
  if (!versions?.length) return null;
  return versions.reduce((max, item) => {
    if (!max || item.version > max.version) return item;
    return max;
  }, null);
}

/** List badge status derived from the max version's API status. */
export function getWorkflowListStatus(workflow) {
  const maxVersion = getMaxVersion(workflow);
  if (maxVersion?.status) {
    return VERSION_STATUS_TO_BADGE[maxVersion.status] || maxVersion.status;
  }
  if (workflow?.status === "ARCHIVED") return "ARCHIVED";
  if (workflow?.draftVersion) return "DRAFT";
  if (workflow?.activeVersion) return "LIVE";
  return "DRAFT";
}

export function canSetWorkflowTrigger(workflow) {
  return !!workflow?.activeVersion;
}

/** Active published version entry from `versions[]`, if present. */
export function getActiveVersionEntry(workflow) {
  if (!workflow?.activeVersion || !workflow.versions?.length) return null;
  return workflow.versions.find((item) => item.version === workflow.activeVersion) ?? null;
}

/** True when the workflow has a live ACTIVE version that can be paused. */
export function canPauseWorkflow(workflow) {
  if (!workflow?.activeVersion) return false;
  const activeEntry = getActiveVersionEntry(workflow);
  if (activeEntry?.status) return activeEntry.status === "ACTIVE";
  return true;
}

/** Workflows eligible for the default trigger picker (published, not archived). */
export function getLiveWorkflows(workflows) {
  return (workflows || []).filter(
    (workflow) => !!workflow.activeVersion && workflow.status !== "ARCHIVED"
  );
}

/** Resolves the DEFAULT (fallback) trigger route from GET_TRIGGER_CONFIG response. */
export function getDefaultTriggerFromConfig(config) {
  if (!config?.intents?.length) return null;

  const fallbackIntent = config.fallbackIntent || "DEFAULT";
  return (
    config.intents.find((item) => item.intent === fallbackIntent)
    ?? config.intents.find((item) => item.intent === "DEFAULT")
    ?? config.intents[0]
  );
}

/** Payload for SAVE_TRIGGER_CONFIG when setting a single DEFAULT intent trigger. */
export function buildDefaultTriggerConfigPayload(workflowId, workflowVersion) {
  return {
    intents: [
      {
        intent: "DEFAULT",
        workflowId,
        workflowVersion
      }
    ],
    fallbackIntent: "DEFAULT"
  };
}

/** Version used for analytics default: active → draft → highest listed. */
export function getPreferredWorkflowVersion(workflow) {
  if (workflow.activeVersion != null) return workflow.activeVersion;
  if (workflow.draftVersion != null) return workflow.draftVersion;
  return getMaxVersion(workflow)?.version ?? null;
}

/** `{ value, label }` options for workflow version `<Select>`. */
export function getWorkflowVersionSelectOptions(workflow) {
  if (workflow.versions?.length) {
    return [...workflow.versions]
      .sort((a, b) => b.version - a.version)
      .map((item) => ({
        value: String(item.version),
        label: `v${item.version}${item.status ? ` (${item.status})` : ""}`
      }));
  }
  const version = getPreferredWorkflowVersion(workflow);
  if (version == null) return [];
  return [{ value: String(version), label: `v${version}` }];
}
